// Destination model for the skip-logic section builder.
//
// A rule's "go to" - and a section's "otherwise" route - may only move the
// respondent FORWARD: to a later question or content screen, or to the end of
// the survey. Earlier items, the section's own slot, another skip-logic section
// and unknown items are rejected; the engine treats such a destination as a
// fallback, and the builder must not let one be authored. This is the single
// source of that forward-only rule so the destination picker and the publish
// guard agree.
//
// A destination is the rules-JSON shape: { next: true } | { end: true } |
// { item: <id> }, where the id is the server's integer item id. Items are
// matched by `itemId`; unsaved items have none and cannot be referenced.
// Problem codes match the server's RouteProblemCodes so builder-time checks and
// server-time validation name the same failure the same way.

import {ITEM_TYPE} from '../constants'

const indexOfSection = (survey, sectionId) =>
  (survey?.items || []).findIndex(
    item => item?.itemType === ITEM_TYPE.SKIP_LOGIC && item.itemId === sectionId
  )

// The section's slot. An explicit array index wins over an id lookup: while a
// section is unsaved it has no id, and several unsaved sections would all
// resolve to the first null-id match, so the builder passes the editing
// section's index to keep "later than this section" correct.
const sectionSlot = (survey, sectionId, sectionIndex) =>
  Number.isInteger(sectionIndex) && sectionIndex >= 0
    ? sectionIndex
    : indexOfSection(survey, sectionId)

// The items after the given section, in document order - the candidates a rule
// in that section may route to. A later item may itself be a skip-logic section
// (that is how sections chain), but it is not a valid destination. Empty when
// the section is missing or last.
export const laterItems = (survey, sectionId, sectionIndex) => {
  const at = sectionSlot(survey, sectionId, sectionIndex)
  if (at < 0) return []
  return (survey?.items || []).slice(at + 1)
}

// The question items before the given section - the only valid subjects for a
// rule's conditions (a condition may only test an earlier question). Content
// screens, skip-logic sections and not-yet-saved items are excluded.
export const earlierQuestions = (survey, sectionId, sectionIndex) => {
  const at = sectionSlot(survey, sectionId, sectionIndex)
  if (at <= 0) return []
  return (survey?.items || [])
    .slice(0, at)
    .filter(
      item =>
        item.itemId != null &&
        item.itemType !== ITEM_TYPE.CONTENT &&
        item.itemType !== ITEM_TYPE.SKIP_LOGIC
    )
}

// The options a destination picker offers: every persisted later question or
// content screen, then "End of survey". Each option carries the full item (null
// for end) so the UI can label it, and the goTo it stands for. A later section
// is not offered (a destination may not be a section), and a not-yet-saved item
// has no id and cannot be routed to, so both are left out.
export const destinationOptions = (survey, sectionId, sectionIndex) => [
  ...laterItems(survey, sectionId, sectionIndex)
    .filter(
      item => item.itemId != null && item.itemType !== ITEM_TYPE.SKIP_LOGIC
    )
    .map(item => ({item, goTo: {item: item.itemId}})),
  {item: null, goTo: {end: true}},
]

// Validate a destination against the forward-only rule. "next" and "end" are
// always valid; an "item" destination must name a later question or content
// screen that exists. Returns {valid:true} or {valid:false, code}, using the
// server's codes, checked in its order: missing, then is-skip-logic, then
// not-forward.
export const validateDestination = (survey, sectionId, goTo, sectionIndex) => {
  if (!goTo || goTo.next === true || goTo.end === true) return {valid: true}
  if (goTo.item == null) return {valid: false, code: 'destination-missing'}
  const target = (survey?.items || []).find(item => item.itemId === goTo.item)
  if (!target) return {valid: false, code: 'destination-missing'}
  if (target.itemType === ITEM_TYPE.SKIP_LOGIC) {
    return {valid: false, code: 'destination-is-skip-logic'}
  }
  const isLater = laterItems(survey, sectionId, sectionIndex).some(
    item => item.itemId === goTo.item
  )
  return isLater ? {valid: true} : {valid: false, code: 'destination-not-forward'}
}
